import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { SiteCompareItem, SiteCompareResponse } from '@/lib/api.types';
import { candidateStore } from '@/lib/candidate-store';
import { rankToStatus, rankToStatusLabel } from '../chapters/shared';
import styles from './CompareModal.module.css';

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function CompareModal({ open, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SiteCompareResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    const sites = candidateStore.list();
    if (sites.length < 2) {
      setError('比較するには候補地を 2 件以上保存してください。');
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .compare({
        sites: sites.map((s) => ({
          label: s.label,
          point: s.point,
          address: s.address,
        })),
        include_analysis: false,
      })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : '比較に失敗しました');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.root} role="dialog" aria-modal="true" aria-label="候補地の比較">
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.modal}>
        <header className={styles.header}>
          <h2>候補地の比較</h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          {loading && <div className={styles.state}>読み込み中…</div>}
          {error && <div className={styles.error}>{error}</div>}
          {data && (
            <>
              <p className={styles.note}>{data.note}</p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>候補地</th>
                      <th>総合の注意度</th>
                      <th>地盤</th>
                      <th>災害</th>
                      <th>建築/都市計画</th>
                      <th>要点</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item: SiteCompareItem, i) => (
                      <tr key={`${item.label}-${i}`}>
                        <td className={styles.cellLabel}>
                          <strong>{item.label}</strong>
                          {item.address && <div className={styles.cellSub}>{item.address}</div>}
                        </td>
                        <td>
                          <Chip rank={item.overall_rank} text={item.overall_label} />
                        </td>
                        <td>
                          <Chip rank={item.ground_rank} text={rankToStatusLabel(item.ground_rank)} />
                        </td>
                        <td>
                          <Chip rank={item.hazard_rank} text={rankToStatusLabel(item.hazard_rank)} />
                        </td>
                        <td>
                          <Chip rank={item.planning_rank} text={rankToStatusLabel(item.planning_rank)} />
                        </td>
                        <td className={styles.cellHighlights}>
                          {item.highlights.length === 0 ? (
                            <span className={styles.muted}>--</span>
                          ) : (
                            <ul>
                              {item.highlights.map((h, j) => (
                                <li key={j}>{h}</li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ rank, text }: { rank: import('@/lib/api.types').RiskLevel; text: string }) {
  const kind = rankToStatus(rank);
  return <span className={`${styles.chip} ${styles[`chip-${kind}`]}`}>{text}</span>;
}
